import type { LocalizedProductInfoFields, Product } from '@shared/gql/document-nodes'

import {
  BarcodeOutline24,
  BusinessOutline24,
  Button,
  ButtonType,
  ChecklistOutline24,
  ForbiddenOutline24,
  WebOutline24,
} from '@sok/design-system'
import { BlockTitle } from 'components/common/Titles'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'
import { useParsedMarkdown } from 'utils/hooks/use-parsed-markdown'

type PickedProductFields = Pick<
  Product,
  | 'name'
  | 'description'
  | 'ingredientStatement'
  | 'countryName'
  | 'countryOfMainRawMaterial'
  | 'supplierName'
  | 'ean'
>

interface ProductDescriptionProps {
  product: PickedProductFields
  localizedFields?: LocalizedProductInfoFields | null
}

const StyledProductDescription = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledProductDescriptionItem = styled.div(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacings.small,
  '> div': {
    marginLeft: '0.5rem',
  },
}))

const StyledTextContent = styled.div`
  ${({ theme }) => css(theme.variants.body2.regular)};

  ol,
  ul {
    list-style-position: inside;
  }

  a {
    color: ${({ theme }) => theme.color.action};
  }

  p + p {
    margin-top: ${({ theme }) => theme.spacings.xxSmall};
  }
`

const StyledReadMoreButton = styled(Button)({
  padding: '0 !important',
})

const MAX_DESCRIPTION_LENGTH = 150

const sliceDescription = (description: string | null): string | null => {
  if (!description || (description && description.length <= MAX_DESCRIPTION_LENGTH)) {
    return description
  }
  const sliced = description.slice(0, MAX_DESCRIPTION_LENGTH)
  const slicedToNearestFullWord = sliced
    .slice(0, Math.min(MAX_DESCRIPTION_LENGTH, sliced.lastIndexOf(' ')))
    .concat('…')
  return slicedToNearestFullWord
}

export const ProductDescription = ({ product, localizedFields }: ProductDescriptionProps) => {
  const isClientSide = useIsClientSide()
  const { t } = useTranslation()
  const descriptionText = localizedFields?.description ?? product.description
  const isLongDescription =
    isClientSide && !!descriptionText?.length && descriptionText.length > MAX_DESCRIPTION_LENGTH

  /* `description` is initialized with full text for SEO, then sliced in effect
    so it takes less room on the screen for "better UX" */
  const [readMore, setReadMore] = useState(false)
  const description = useMemo(
    () => (readMore ? descriptionText : sliceDescription(descriptionText)),
    [descriptionText, readMore],
  )

  const parsedDescription = useParsedMarkdown(description)

  /**
   * Some suppliers do not set product descriptions, or only input it as the dash `-`.
   * Use the product name as a fallback when description seems to be missing.
   *
   * @see https://jira.sok.fi/browse/VOIK-9884
   */
  const displayedDescription =
    product.description && product.description.trim().length > 1 ? parsedDescription : product.name

  const parsedAllergens = useParsedMarkdown(
    localizedFields?.allergens
      ?.map((allergen) => allergen.allergenTypeText)
      .filter(Boolean)
      .join(', '),
  )

  const parsedIngredients = useParsedMarkdown(
    localizedFields?.ingredientStatement ?? product.ingredientStatement,
  )

  const productContents = useMemo(
    () =>
      [
        {
          dataTestId: 'product-info-description',
          value: displayedDescription,
          markdown: true,
          enableReadMore: isLongDescription,
        },
        {
          dataTestId: 'product-info-country',
          title: t('Country of Manufacture'),
          value: product.countryName?.fi,
          icon: <WebOutline24 />,
        },
        {
          dataTestId: 'product-info-country-of-main-raw-material',
          title: t('Country of main raw material'),
          value: product.countryOfMainRawMaterial,
          icon: <WebOutline24 />,
        },
        {
          dataTestId: 'product-info-allergens',
          title: t('Allergens'),
          value: parsedAllergens,
          markdown: true,
          icon: <ForbiddenOutline24 />,
        },
        {
          dataTestId: 'product-info-ingredients',
          title: t('Ingredients'),
          value: parsedIngredients,
          markdown: true,
          icon: <ChecklistOutline24 />,
        },
        {
          dataTestId: 'product-info-supplier',
          title: t('Supplier name'),
          value: product.supplierName,
          icon: <BusinessOutline24 />,
        },
        {
          dataTestId: 'product-info-ean',
          title: t('EAN'),
          value: product.ean,
          icon: <BarcodeOutline24 />,
        },
      ].filter(({ value }) => !!value),
    [
      displayedDescription,
      isLongDescription,
      parsedAllergens,
      parsedIngredients,
      product.countryName?.fi,
      product.countryOfMainRawMaterial,
      product.ean,
      product.supplierName,
      t,
    ],
  )

  return (
    <StyledProductDescription>
      {productContents.map(({ value, title, dataTestId, markdown, enableReadMore, icon }, idx) => (
        <StyledProductDescriptionItem key={idx} data-test-id={dataTestId}>
          {icon}
          <div>
            {title && <BlockTitle>{title}</BlockTitle>}
            {
              <StyledTextContent>
                {markdown ? value : String(value)}
                {enableReadMore && (
                  <StyledReadMoreButton
                    type={ButtonType.TEXT}
                    onClick={() => setReadMore(!readMore)}
                    text={readMore ? t('Read less') : t('Read more')}
                  />
                )}
              </StyledTextContent>
            }
          </div>
        </StyledProductDescriptionItem>
      ))}
    </StyledProductDescription>
  )
}
